import { useState, useEffect } from 'react'
import api from '../../utils/axiosConfig'

const RegistrationRequestsModal = ({ onClose }) => {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(null)
  const [showRejectModal, setShowRejectModal] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showDetail, setShowDetail] = useState(null)

  useEffect(() => {
    fetchRequests()
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  const fetchRequests = async () => {
    try {
      setLoading(true)
      const response = await api.get('/user/registration-requests')
      if (response.data.success) {
        setRequests(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching requests:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (id) => {
    if (!window.confirm('Are you sure? A new player will be created under your account.')) return
    setProcessing(id)
    try {
      const response = await api.post(`/user/registration-requests/${id}/approve`)
      if (response.data.success) {
        alert('Player registration approved!')
        fetchRequests()
        setShowDetail(null)
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to approve')
    } finally {
      setProcessing(null)
    }
  }

  const handleReject = async (id) => {
    setProcessing(id)
    try {
      const response = await api.post(`/user/registration-requests/${id}/reject`, { reason: rejectReason })
      if (response.data.success) {
        alert('Request rejected.')
        setShowRejectModal(null)
        setRejectReason('')
        fetchRequests()
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to reject')
    } finally {
      setProcessing(null)
    }
  }

  const formatDate = (d) => {
    if (!d) return '-'
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  const statusStyle = {
    pending: { bg: '#fef3c7', color: '#92400e' },
    approved: { bg: '#d1fae5', color: '#065f46' },
    rejected: { bg: '#fee2e2', color: '#991b1b' },
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length

  return (
    <div className="rrm-overlay" onClick={onClose}>
      <div className="rrm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="rrm-header">
          <div className="rrm-header-left">
            <i className="fas fa-clipboard-check"></i>
            <div>
              <h2>Registration Requests</h2>
              <p>{pendingCount > 0 ? `${pendingCount} pending` : 'No pending requests'}</p>
            </div>
          </div>
          <button className="rrm-close" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>

        {/* Body */}
        <div className="rrm-body">
          {loading ? (
            <div className="rrm-empty"><i className="fas fa-spinner fa-spin"></i><p>Loading...</p></div>
          ) : requests.length === 0 ? (
            <div className="rrm-empty"><i className="fas fa-inbox"></i><h3>No Requests</h3><p>No player registration requests found.</p></div>
          ) : (
            <div className="rrm-list">
              {requests.map(req => (
                <div key={req.id} className={`rrm-card rrm-${req.status}`}>
                  <div className="rrm-card-top">
                    <div className="rrm-card-info">
                      <h4>{req.full_name}</h4>
                      <div className="rrm-meta">
                        <span>{(req.member_type || 'player').charAt(0).toUpperCase() + (req.member_type || 'player').slice(1)}</span>
                        <span>{formatDate(req.created_at)}</span>
                        {req.requester_name && <span>By: {req.requester_name}</span>}
                      </div>
                    </div>
                    <span className="rrm-badge" style={{ background: statusStyle[req.status]?.bg, color: statusStyle[req.status]?.color }}>
                      {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                    </span>
                  </div>

                  <div className="rrm-chips">
                    {req.father_name && <span className="rrm-chip">S/o {req.father_name}</span>}
                    <span className="rrm-chip">{formatDate(req.date_of_birth)}</span>
                    <span className="rrm-chip">{req.gender === 'M' ? 'Male' : 'Female'}</span>
                    {req.state_name && <span className="rrm-chip">{req.state_name}</span>}
                    {req.category && <span className="rrm-chip">{req.category}</span>}
                  </div>

                  <div className="rrm-card-actions">
                    <button className="rrm-btn rrm-btn-view" onClick={() => setShowDetail(req)}>
                      <i className="fas fa-eye"></i> Details
                    </button>
                    {req.status === 'pending' && (
                      <>
                        <button className="rrm-btn rrm-btn-approve" onClick={() => handleApprove(req.id)} disabled={processing === req.id}>
                          {processing === req.id ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>} Approve
                        </button>
                        <button className="rrm-btn rrm-btn-reject" onClick={() => setShowRejectModal(req.id)} disabled={processing === req.id}>
                          <i className="fas fa-times"></i> Reject
                        </button>
                      </>
                    )}
                  </div>

                  {req.status === 'rejected' && req.rejection_reason && (
                    <div className="rrm-rejection"><i className="fas fa-info-circle"></i> {req.rejection_reason}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Sub-Modal */}
      {showDetail && (
        <div className="rrm-sub-overlay" onClick={() => setShowDetail(null)}>
          <div className="rrm-sub-modal" onClick={e => e.stopPropagation()}>
            <div className="rrm-sub-header">
              <h3><i className="fas fa-user-circle"></i> Player Details</h3>
              <button onClick={() => setShowDetail(null)}><i className="fas fa-times"></i></button>
            </div>
            <div className="rrm-sub-body">
              <Row label="Full Name" value={showDetail.full_name} />
              <Row label="Father's Name" value={showDetail.father_name} />
              <Row label="Member Type" value={(showDetail.member_type || 'player').charAt(0).toUpperCase() + (showDetail.member_type || 'player').slice(1)} />
              <Row label="Date of Birth" value={formatDate(showDetail.date_of_birth)} />
              <Row label="Gender" value={showDetail.gender === 'M' ? 'Male' : 'Female'} />
              <Row label="Category" value={showDetail.category} />
              <Row label="Blood Group" value={showDetail.blood_group} />
              <Row label="Height" value={showDetail.height ? `${showDetail.height} cm` : null} />
              <Row label="Weight" value={showDetail.weight ? `${showDetail.weight} kg` : null} />
              <Row label="State" value={showDetail.state_name} />
              <Row label="District" value={showDetail.district} />
              <Row label="Address" value={showDetail.address} />
              <Row label="PIN Code" value={showDetail.pin_code} />
              <Row label="Contact" value={showDetail.contact} />
              <Row label="Emergency Contact" value={showDetail.emergency_contact} />
              <Row label="Email" value={showDetail.email} />
              <Row label="Position" value={showDetail.position} />
              <Row label="Aadhar" value={showDetail.aadhar_card} />
              <Row label="Passport" value={showDetail.passport_number} />
              <Row label="Marital Status" value={showDetail.marital_status} />
              {showDetail.requester_name && <Row label="Requested By" value={`${showDetail.requester_name}${showDetail.requester_contact ? ' (' + showDetail.requester_contact + ')' : ''}`} />}
              <Row label="Submitted On" value={formatDate(showDetail.created_at)} />
            </div>
            {showDetail.status === 'pending' && (
              <div className="rrm-sub-footer">
                <button className="rrm-btn rrm-btn-approve" onClick={() => handleApprove(showDetail.id)} disabled={processing === showDetail.id}>
                  <i className="fas fa-check"></i> Approve
                </button>
                <button className="rrm-btn rrm-btn-reject" onClick={() => { setShowDetail(null); setShowRejectModal(showDetail.id); }}>
                  <i className="fas fa-times"></i> Reject
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reject Sub-Modal */}
      {showRejectModal && (
        <div className="rrm-sub-overlay" onClick={() => setShowRejectModal(null)}>
          <div className="rrm-sub-modal rrm-sub-small" onClick={e => e.stopPropagation()}>
            <div className="rrm-sub-header">
              <h3><i className="fas fa-times-circle" style={{ color: '#ef4444' }}></i> Reject Request</h3>
              <button onClick={() => { setShowRejectModal(null); setRejectReason(''); }}><i className="fas fa-times"></i></button>
            </div>
            <div className="rrm-sub-body">
              <p style={{ marginBottom: '1rem', color: '#374151' }}>Are you sure you want to reject this request?</p>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', color: '#374151', fontSize: '0.9rem' }}>Reason (Optional)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows="3"
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.9rem', resize: 'vertical' }}
              />
            </div>
            <div className="rrm-sub-footer">
              <button className="rrm-btn rrm-btn-view" onClick={() => { setShowRejectModal(null); setRejectReason(''); }}>Cancel</button>
              <button className="rrm-btn rrm-btn-reject" onClick={() => handleReject(showRejectModal)} disabled={processing}>
                {processing ? <><i className="fas fa-spinner fa-spin"></i> Rejecting...</> : <><i className="fas fa-times"></i> Reject</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .rrm-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
          animation: rrmFadeIn 0.2s ease;
        }
        @keyframes rrmFadeIn { from { opacity: 0; } to { opacity: 1; } }

        .rrm-modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 700px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          animation: rrmSlideUp 0.3s ease;
        }
        @keyframes rrmSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

        .rrm-header {
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }
        .rrm-header-left { display: flex; align-items: center; gap: 0.75rem; }
        .rrm-header-left i { font-size: 1.5rem; color: var(--primary-color, #022a5e); }
        .rrm-header-left h2 { margin: 0; font-size: 1.2rem; color: #111827; }
        .rrm-header-left p { margin: 0; font-size: 0.8rem; color: #6b7280; }
        .rrm-close {
          width: 36px; height: 36px; border-radius: 50%; border: none;
          background: #f3f4f6; cursor: pointer; display: flex; align-items: center;
          justify-content: center; color: #6b7280; font-size: 1rem; transition: all 0.2s;
        }
        .rrm-close:hover { background: #e5e7eb; color: #111827; }

        .rrm-body {
          overflow-y: auto;
          padding: 1rem 1.5rem;
          flex: 1;
        }

        .rrm-empty {
          text-align: center;
          padding: 3rem 1rem;
          color: #9ca3af;
        }
        .rrm-empty i { font-size: 2.5rem; display: block; margin-bottom: 0.75rem; }
        .rrm-empty h3 { color: #374151; margin: 0.5rem 0 0.25rem; }
        .rrm-empty p { margin: 0; }

        .rrm-list { display: flex; flex-direction: column; gap: 0.75rem; }

        .rrm-card {
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 1rem 1.25rem;
          border-left: 4px solid #e5e7eb;
          transition: all 0.2s;
        }
        .rrm-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
        .rrm-pending { border-left-color: #f59e0b; }
        .rrm-approved { border-left-color: #10b981; }
        .rrm-rejected { border-left-color: #ef4444; }

        .rrm-card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem; }
        .rrm-card-info h4 { margin: 0; font-size: 1rem; color: #111827; }
        .rrm-meta { display: flex; gap: 0.75rem; margin-top: 0.2rem; flex-wrap: wrap; }
        .rrm-meta span { font-size: 0.75rem; color: #6b7280; }

        .rrm-badge {
          padding: 0.2rem 0.6rem;
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 600;
          flex-shrink: 0;
        }

        .rrm-chips { display: flex; gap: 0.4rem; flex-wrap: wrap; margin-bottom: 0.75rem; }
        .rrm-chip {
          background: #f3f4f6;
          padding: 0.2rem 0.6rem;
          border-radius: 999px;
          font-size: 0.75rem;
          color: #4b5563;
        }

        .rrm-card-actions { display: flex; gap: 0.4rem; flex-wrap: wrap; }

        .rrm-btn {
          padding: 0.4rem 0.85rem;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.8rem;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          border: none;
          transition: all 0.2s;
        }
        .rrm-btn-view { background: #f3f4f6; color: #374151; }
        .rrm-btn-view:hover { background: #e5e7eb; }
        .rrm-btn-approve { background: #10b981; color: white; }
        .rrm-btn-approve:hover:not(:disabled) { background: #059669; }
        .rrm-btn-reject { background: #ef4444; color: white; }
        .rrm-btn-reject:hover:not(:disabled) { background: #dc2626; }
        .rrm-btn:disabled { opacity: 0.7; cursor: not-allowed; }

        .rrm-rejection {
          margin-top: 0.5rem;
          padding: 0.4rem 0.6rem;
          background: #fef2f2;
          border-radius: 6px;
          font-size: 0.8rem;
          color: #991b1b;
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }

        /* Sub-modals (detail & reject) */
        .rrm-sub-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
          padding: 1rem;
        }
        .rrm-sub-modal {
          background: white;
          border-radius: 14px;
          width: 100%;
          max-width: 550px;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .rrm-sub-small { max-width: 420px; }
        .rrm-sub-header {
          padding: 1rem 1.25rem;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .rrm-sub-header h3 {
          margin: 0; font-size: 1.1rem; display: flex; align-items: center; gap: 0.5rem;
        }
        .rrm-sub-header button {
          background: none; border: none; font-size: 1.1rem; color: #6b7280; cursor: pointer;
        }
        .rrm-sub-body {
          padding: 1.25rem;
          overflow-y: auto;
          flex: 1;
        }
        .rrm-sub-footer {
          padding: 1rem 1.25rem;
          border-top: 1px solid #e5e7eb;
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
        }

        .rrm-detail-row {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0.6rem;
          border-radius: 6px;
        }
        .rrm-detail-row:nth-child(odd) { background: #f9fafb; }
        .rrm-detail-row .rrm-dl { font-size: 0.82rem; color: #6b7280; font-weight: 500; }
        .rrm-detail-row .rrm-dv { font-size: 0.82rem; color: #111827; font-weight: 600; text-align: right; }

        @media (max-width: 640px) {
          .rrm-modal { max-height: 95vh; border-radius: 12px; }
          .rrm-card-actions { flex-direction: column; }
          .rrm-btn { width: 100%; justify-content: center; }
          .rrm-detail-row { flex-direction: column; gap: 0.15rem; }
          .rrm-detail-row .rrm-dv { text-align: left; }
        }
      `}</style>
    </div>
  )
}

const Row = ({ label, value }) => {
  if (!value) return null
  return (
    <div className="rrm-detail-row">
      <span className="rrm-dl">{label}</span>
      <span className="rrm-dv">{value}</span>
    </div>
  )
}

export default RegistrationRequestsModal
